import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Space } from '../types/venue';

type SpacesResponse = Space[];
type DefaultError = unknown;

type UseSpacesParams = {
  pageNumber?: number;
  pageSize?: number;
  /**
   * Comma-separated list of relations, e.g. 'venue'
   * This matches your `include` query param that `parseInclude` reads.
   */
  include?: string;
  /**
   * Filter parameters as key-value pairs.
   * Keys should be in the format 'filter[field]' or 'filter[relation.field]'
   * Example: { 'filter[venue.deletedAt]': 'null' }
   */
  filters?: Record<string, string>;
  /** Override default enabled behavior */
  enabled?: boolean;
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | null
    | Record<string, string>;
};

/**
 * useSpaces
 *
 * @example
 * const spacesQuery = useSpaces(
 *   {
 *     pageNumber: 1,
 *     pageSize: 10,
 *     include: 'venue',
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useSpaces<TData = SpacesResponse, TError = DefaultError>(
  {
    pageNumber = 1,
    pageSize = 10,
    include,
    filters,
    enabled = true,
    ...restParams
  }: UseSpacesParams = {},
  options?: Omit<
    UseQueryOptions<SpacesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = [
    'spaces',
    pageNumber,
    pageSize,
    include,
    filters,
    restParams,
  ];

  return useQuery<SpacesResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () =>
      service.getSpaces({
        pageNumber,
        pageSize,
        ...(include ? { include } : {}),
        ...(filters || {}),
        ...(restParams ? { ...restParams } : {}),
      }),
    ...(options as any),
  });
}
